import { Effect } from 'postprocessing';
import { Uniform, Vector2 } from 'three';

const fragmentShader = /* glsl */ `
  uniform float uStrength;
  uniform float uZoom;
  uniform vec2 uAspect;

  void mainUv(inout vec2 uv) {
    vec2 c = (uv - 0.5) * uAspect;
    float r2 = dot(c, c);
    // >0 bulges (content spreads outward from the centre), <0 pinches.
    c *= (1.0 - uStrength * r2) / uZoom;
    uv = clamp(c / uAspect + 0.5, 0.0, 1.0);
  }
`;

export interface FisheyeEffectOptions {
  strength?: number;
  zoom?: number;
}

export class FisheyeEffect extends Effect {
  constructor({ strength = 0.25, zoom = 1 }: FisheyeEffectOptions = {}) {
    super('FisheyeEffect', fragmentShader, {
      uniforms: new Map<string, Uniform<number | Vector2>>([
        ['uStrength', new Uniform(strength)],
        ['uZoom', new Uniform(zoom)],
        ['uAspect', new Uniform(new Vector2(1, 1))],
      ]),
    });
  }

  get strength(): number {
    return this.uniforms.get('uStrength')!.value as number;
  }

  set strength(value: number) {
    this.uniforms.get('uStrength')!.value = value;
  }

  get zoom(): number {
    return this.uniforms.get('uZoom')!.value as number;
  }

  set zoom(value: number) {
    this.uniforms.get('uZoom')!.value = value;
  }

  override setSize(width: number, height: number): void {
    // Normalise the longer axis to 1 so the distortion is a circle, not an
    // ellipse, at any viewport shape.
    const aspect = this.uniforms.get('uAspect')!.value as Vector2;
    if (width >= height) {
      aspect.set(width / height, 1);
    } else {
      aspect.set(1, height / width);
    }
  }
}
